// The panel's checkbox, drawn rather than left to the browser: a hairline box
// that fills with brand cyan and shows a white tick once ticked.
//
// The native input is kept underneath — it carries the role, the label
// association and the keyboard behaviour — and only its paint is replaced, so
// the control still reads as a checkbox to tests and to screen readers.

import React from "react";

export interface CheckboxProps {
  /** Takes a node, not just a string, so a label can carry its own links. */
  label: React.ReactNode;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  /** Second line under the label, for a tick that needs explaining. Its
   *  presence also switches the row from centred to top-aligned. */
  description?: React.ReactNode;
  /** Appended to the label's own classes, so it wins - for rows that need a
   *  different size or colour from the form default. */
  labelClassName?: string;
  className?: string;
}

export const Checkbox: React.FC<CheckboxProps> = ({
  label,
  checked,
  onCheckedChange,
  description,
  labelClassName = "",
  className = "",
}) => {
  const fieldId = React.useId();

  return (
    <div
      className={`flex gap-2 ${
        description ? "items-start" : "items-center"
      } ${className}`}
    >
      <span
        className={`relative inline-flex h-4 w-4 flex-shrink-0 items-center justify-center ${
          description ? "mt-[3px]" : ""
        }`}
      >
        <input
          id={fieldId}
          type="checkbox"
          checked={checked}
          onChange={(event) => onCheckedChange(event.target.checked)}
          className="peer h-4 w-4 cursor-pointer appearance-none rounded-[2.5px] border-[0.6px] border-[#8B8D98] bg-white transition-colors checked:border-[#00C5D7] checked:bg-[#00C5D7] focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/40"
        />
        {/* A sibling of the input, so the tick can follow `:checked` without
            the component having to re-render to swap an icon. */}
        <svg
          viewBox="0 0 12 10"
          fill="none"
          aria-hidden="true"
          className="pointer-events-none absolute h-[10px] w-[10px] text-white opacity-0 transition-opacity peer-checked:opacity-100"
        >
          <path
            d="M1 5.1 4.2 8.3 11 1.5"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </span>
      <div>
        <label
          htmlFor={fieldId}
          className={`cursor-pointer select-none text-[13px] 3xl:text-[14px] text-ink-muted ${labelClassName}`}
        >
          {label}
        </label>
        {description && (
          <p className="mt-1 text-[13px] leading-5 text-ink-muted">
            {description}
          </p>
        )}
      </div>
    </div>
  );
};

export default Checkbox;
